import Timer from '../tnew/lib/Timer';
import Messaging from '../tnew/ui/Messaging';
import CartTimerService from '../cart/timer/CartTimerService';
import CartExpiredModal from '../cart/timer/CartExpiredModal';
import announce from '../utils/screen-reader/announce';
import dom from '../utils/dom';
import type ICartTimerHandlerElements from './ICartTimerHandlerElements';

const Class = {
  urgent: 'tn-utility-nav-cart-timer--urgent',
};

function setErrorMessage(message: string) {
  Messaging.clear();
  Messaging.addError(message, true);
}

function getNumberRange(min = 0, max = 5, increment = 1) {
  const values: number[] = [];
  let current = min;

  if (min > max) {
    return values;
  }

  values.push(current);

  while (current + increment <= max) {
    current += increment;

    values.push(current);
  }

  return values;
}

function getUniqueArray(value: number[]) : number[] {
  return [...new Set(value).values()];
}

function getAnnouncementIntervals(totalSeconds: number, delay: number) {
  const startTime = totalSeconds - delay;

  const staticIntervals = [
    7 * 60,
    5 * 60,
    3 * 60,
    2 * 60,
    1.5 * 60,
    60,
    30,
    20,
    10,
    5,
  ];

  const intervalsTenMinutesAndAbove = getNumberRange(10 * 60, startTime, 5 * 60);

  const allIntervals = [
    startTime,
    ...staticIntervals,
    ...intervalsTenMinutesAndAbove,
  ];

  return getUniqueArray(allIntervals)
    .sort((a, b) => b - a)
    .filter((el) => el <= startTime);
}

function getAnnouncementFormattedTime(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const seconds = Math.floor(totalSeconds % 60);
  const timeParts = [];

  function numberEnding(value: number) {
    return (value > 1) ? 's' : '';
  }

  if (hours > 0) {
    timeParts.push(`${hours} hour${numberEnding(hours)}`);
  }

  if (minutes > 0) {
    timeParts.push(`${minutes} minute${numberEnding(minutes)}`);
  }

  if (seconds > 0) {
    timeParts.push(`${seconds} second${numberEnding(seconds)}`);
  }

  return timeParts.join(' ');
}

class CartTimerHandler {
  static instance: CartTimerHandler;

  $el: HTMLDivElement;

  $countdown: HTMLSpanElement;

  $utilityNav: HTMLDivElement;

  timer: Timer | undefined;

  announcementIntervals: number[] = [];

  secondsRemaining: number = Infinity;

  constructor(elements: ICartTimerHandlerElements) {
    const { $el, $countdown, $utilityNav } = elements.getAll();

    this.$el = $el;
    this.$countdown = $countdown;
    this.$utilityNav = $utilityNav;

    if (!$el) {
      return;
    }

    const isTimed = $el.hasAttribute('data-tn-is-timed');

    if (isTimed) {
      const milliseconds = Number.parseFloat(this.$countdown.getAttribute('data-tn-milliseconds-remaining')!);

      this.setTimeRemaining(milliseconds, 2);
    }
  }

  checkForAnnouncement(secondsRemaining: number) {
    const { announcementIntervals } = this;

    if (announcementIntervals.length <= 0) {
      return;
    }

    if (secondsRemaining <= announcementIntervals[0]) {
      announce(`Less than ${getAnnouncementFormattedTime(announcementIntervals[0])} remaining before your order expires.`);

      announcementIntervals.shift();
    }
  }

  setTimeRemaining(milliseconds: number, announcementDelaySeconds = 0) {
    this.announcementIntervals = getAnnouncementIntervals(milliseconds / 1000, announcementDelaySeconds);

    if (!this.timer) {
      this.$utilityNav.classList.add('tn-utility-nav--timer-active');

      this.timer = new Timer({
        duration: milliseconds,
        onUpdate: this.onUpdate,
        onComplete: this.onComplete,
      });
    }

    this.timer.restart(milliseconds);
  }

  onUpdate = (formattedTimeRemaining: string, secondsRemaining: number) => {
    if (secondsRemaining === this.secondsRemaining) {
      return;
    }

    this.checkForAnnouncement(secondsRemaining);

    window.requestAnimationFrame(() => {
      const isUrgent = secondsRemaining < 120;

      this.$el.classList.toggle(Class.urgent, isUrgent);

      this.secondsRemaining = secondsRemaining;

      this.$countdown.innerText = formattedTimeRemaining;

      this.$countdown.style.visibility = 'visible';

      dom.unhide(this.$el);
    });
  };

  onCartExpireAcknowledge = () => {
    window.location.reload();
  };

  transferSession = async () => {
    try {
      await CartTimerService.transferSession();
    } catch (error) {
      setErrorMessage('There was an error clearing the cart');

      return;
    }

    CartExpiredModal.open(this.onCartExpireAcknowledge);
  };

  onComplete = async () => {
    let millisecondsRemaining;

    try {
      const response = await CartTimerService.getTimeRemaining();
      millisecondsRemaining = response.millisecondsRemaining;
    } catch (error) {
      setErrorMessage('There was an error updating the cart timer');

      return;
    }

    if (millisecondsRemaining <= 0) {
      this.transferSession();
    } else {
      this.setTimeRemaining(millisecondsRemaining);
    }
  };
}

export default CartTimerHandler;
